import { buttonVariants } from "@/components/ui/button/button";
import Container from "@/components/ui/Container";
import SubmitButton from "@/components/ui/submit-button";
import SuccessScreen from "@/components/ui/SuccessScreen";
import Typography from "@/components/ui/typography";
import { useAuth } from "@/context/auth-context";
import useCreateIdentityVerificationSession from "@/features/identity-verification/useCreateIdentityVerificationSession";
import { savePendingClearSession } from "@/features/identity-verification/pendingClearSession";
import { isUserFullyVerified } from "@/lib/utils";
import { Link, useNavigate, useLoaderData } from "@tanstack/react-router";
import { PlusIcon, XIcon, CircleCheck } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";

const VerifyStart = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { toast } = useToast();
	const { location_id } = useLoaderData({ from: "__root__" });

	const { mutate: createVerificationSession, status } =
		useCreateIdentityVerificationSession({
			onSuccess: (data) => {
				savePendingClearSession(user.id);
				window.location.href = `https://verified.clearme.com/verify?token=${data.token}`;
			},
			onError: (error) => {
				toast({
					title: "Request Failed",
					description: error.message,
					variant: "destructive",
				});
			},
		});

	function handleCreateSession() {
		createVerificationSession({ user_id: user.id, location_id });
	}

	const isFullyVerified = isUserFullyVerified(user);
	if (isFullyVerified) {
		return (
			<SuccessScreen
				title="Your account has already been verified"
				description="You can now start adding credit cards and converting credit to cash."
				onClose={() => {
					navigate({ to: "/main/home" });
				}}
			>
				<Link
					to={"/main/card/new"}
					className={buttonVariants({
						variant: "default",
					})}
				>
					Add a credit card <PlusIcon />
				</Link>
			</SuccessScreen>
		);
	}

	return (
		<Container className="flex flex-col items-center px-6 py-8 bg-white text-textDark h-[calc(100dvh-8rem)] justify-between">
			<div className="relative flex w-full">
				<XIcon
					className="absolute right-0 -top-2"
					role="button"
					onClick={() =>
						navigate({
							to: "/main/home",
						})
					}
				/>
			</div>
			<div className="flex gap-2">
				<div className="text-center">
					<h1 className="mt-4 mb-2 text-4xl font-bold text-textDark">
						Verify Identity
					</h1>
					<Typography className="max-w-xs" variant={"p"}>
						Take a photo of your driver's license, national identity card, or
						passport. Identity verification is required by law for new users
					</Typography>
				</div>
				{/* <div className="bg-thirdGreen rounded-md flex justify-center items-center mt-2 w-[19px] h-[20px]">
					<InfoSvg />
				</div> */}
			</div>

			<div className="w-full max-w-sm bg-primary-50  p-5 rounded-2xl flex flex-col gap-4 my-6">
				<div className="flex items-start gap-3">
					<CircleCheck className="w-6 h-6 text-white fill-[#00C389] shrink-0" />
					<Typography weight={"normal"} variant={"p"} className="text-sm">
						Use your <strong className="font-semibold">full legal name</strong>{" "}
						and most updated personal information.
					</Typography>
				</div>
				<div className="flex items-start gap-3">
					<CircleCheck className="w-6 h-6 text-white fill-[#00C389] shrink-0" />
					<Typography weight={"normal"} variant={"p"} className="text-sm">
						If you already have a{" "}
						<strong className="font-semibold">CLEAR account</strong>, use the
						same info when verifying with Kashu.
					</Typography>
				</div>
				<div className="flex items-start gap-3">
					<CircleCheck className="w-6 h-6 text-white fill-[#00C389] shrink-0" />
					<Typography weight={"normal"} variant={"p"} className="text-sm">
						Take a{" "}
						<strong className="font-semibold">clear, well-lit photo</strong> of
						your ID — all 4 corners visible, no glare.
					</Typography>
				</div>
			</div>

			<SubmitButton
				type="button"
				mutationStatus={status}
				onClick={handleCreateSession}
				className="w-full"
				loadingText="Starting Verification"
			>
				Verify with CLEAR{" "}
			</SubmitButton>
		</Container>
	);
};

export default VerifyStart;
